import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

// Single source of truth for the theme preference. Kept client-side so the
// toggle is instant and works for both the agent CRM and the client portal
// without any backend churn. Reads OS preference on first load.
const STORAGE_KEY = "ikey_theme";

function readInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch (e) { console.debug("theme read failed", e?.message); }
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  try { localStorage.setItem(STORAGE_KEY, theme); }
  catch (e) { console.debug("theme write failed", e?.message); }
}

/**
 * Compact segmented toggle. Sits inside the profile menu (agent) or the
 * portal header (client). Both surfaces get exactly the same widget so the
 * mental model is identical.
 */
export default function ThemeToggle({ variant = "segmented", testIdPrefix = "theme" }) {
  const [theme, setTheme] = useState(readInitialTheme);

  useEffect(() => { applyTheme(theme); }, [theme]);

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-slate-100"
        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        data-testid={`${testIdPrefix}-toggle-icon`}
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>
    );
  }

  const btn = (val, label, Icon) => {
    const on = theme === val;
    return (
      <button
        type="button"
        onClick={() => setTheme(val)}
        className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded transition-colors ${
          on
            ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white"
            : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
        }`}
        aria-pressed={on}
        data-testid={`${testIdPrefix}-toggle-${val}`}
      >
        <Icon className="h-3.5 w-3.5" /> {label}
      </button>
    );
  };

  return (
    <div className="inline-flex items-center gap-0.5 rounded-md bg-slate-100 dark:bg-slate-800 p-0.5" role="group" aria-label="Theme">
      {btn("light", "Light", Sun)}
      {btn("dark", "Dark", Moon)}
    </div>
  );
}

// Boot-time apply — call once from index.js before React mounts so the initial
// paint already respects the preference (no flash of light mode).
export function bootstrapTheme() {
  applyTheme(readInitialTheme());
}
